/**
 * name: @storyblok/vue
 * (c) 2025
 * description: SDK to integrate Storyblok into your project using Vue.
 * author: Storyblok
 */
import { defineComponent as Y, ref as P, resolveDynamicComponent as N, inject as de, createBlock as W, openBlock as X, mergeProps as pe, h as Q, createTextVNode as fe, watch as ye, onMounted as me, defineAsyncComponent as be } from "vue";
function ge(s, e) {
  if (!e)
    return { src: s, attrs: {} };
  let t = 0, o = 0;
  const r = {}, n = [];
  function a(i, u, f, y, R) {
    typeof i != "number" || i <= u || i >= f ? console.warn(`[StoryblokRichText] - ${y.charAt(0).toUpperCase() + y.slice(1)} value must be a number between ${u} and ${f} (inclusive)`) : R.push(`${y}(${i})`);
  }
  if (typeof e == "object") {
    if (typeof e.width == "number" && e.width > 0 ? (r.width = e.width, t = e.width) : console.warn("[StoryblokRichText] - Width value must be a number greater than 0"), e.height && typeof e.height == "number" && e.height > 0 ? (r.height = e.height, o = e.height) : console.warn("[StoryblokRichText] - Height value must be a number greater than 0"), e.loading && ["lazy", "eager"].includes(e.loading) && (r.loading = e.loading), e.class && (r.class = e.class), e.filters) {
      const { filters: i } = e || {}, { blur: u, brightness: f, fill: y, format: R, grayscale: _, quality: A, rotate: L } = i || {};
      u && a(u, 0, 100, "blur", n), A && a(A, 0, 100, "quality", n), f && a(f, 0, 100, "brightness", n), y && n.push(`fill(${y})`), _ && n.push("grayscale()"), L && [0, 90, 180, 270].includes(e.filters.rotate || 0) && n.push(`rotate(${L})`), R && ["webp", "png", "jpeg"].includes(R) && n.push(`format(${R})`);
    }
    e.srcset && (r.srcset = e.srcset.map((i) => {
      if (typeof i == "number")
        return `${s}/m/${i}x0/${n.length > 0 ? `filters:${n.join(":")}` : ""} ${i}w`;
      if (Array.isArray(i) && i.length === 2) {
        const [u, f] = i;
        return `${s}/m/${u}x${f}/${n.length > 0 ? `filters:${n.join(":")}` : ""} ${u}w`;
      } else {
        console.warn("[StoryblokRichText] - srcset entry must be a number or a tuple of two numbers");
        return;
      }
    }).join(", ")), e.sizes && (r.sizes = e.sizes.join(", "));
  }
  let c = `${s}/m/`;
  return t > 0 && o > 0 && (c = `${c}${t}x${o}/`), n.length > 0 && (c = `${c}filters:${n.join(":")}`), {
    src: c,
    attrs: r
  };
}
var g = /* @__PURE__ */ ((s) => (s.DOCUMENT = "doc", s.HEADING = "heading", s.PARAGRAPH = "paragraph", s.QUOTE = "blockquote", s.OL_LIST = "ordered_list", s.UL_LIST = "bullet_list", s.LIST_ITEM = "list_item", s.CODE_BLOCK = "code_block", s.HR = "horizontal_rule", s.BR = "hard_break", s.IMAGE = "image", s.EMOJI = "emoji", s.COMPONENT = "blok", s.TABLE = "table", s.TABLE_ROW = "tableRow", s.TABLE_CELL = "tableCell", s.TABLE_HEADER = "tableHeader", s))(g || {}), $ = /* @__PURE__ */ ((s) => (s.BOLD = "bold", s.STRONG = "strong", s.STRIKE = "strike", s.UNDERLINE = "underline", s.ITALIC = "italic", s.CODE = "code", s.LINK = "link", s.ANCHOR = "anchor", s.STYLED = "styled", s.SUPERSCRIPT = "superscript", s.SUBSCRIPT = "subscript", s.TEXT_STYLE = "textStyle", s.HIGHLIGHT = "highlight", s))($ || {}), Z = /* @__PURE__ */ ((s) => (s.TEXT = "text", s))(Z || {}), S = /* @__PURE__ */ ((s) => (s.URL = "url", s.STORY = "story", s.ASSET = "asset", s.EMAIL = "email", s))(S || {});
const ve = [
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
], ke = (s = {}) => Object.keys(s).map((e) => `${e}="${s[e]}"`).join(" "), we = (s = {}) => Object.keys(s).map((e) => `${e}: ${s[e]}`).join("; ");
function _e(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
const O = (s) => Object.fromEntries(Object.entries(s).filter(([e, t]) => t !== void 0));
function F(s, e = {}, t) {
  const o = ke(e), r = o ? `${s} ${o}` : s, n = Array.isArray(t) ? t.join("") : t || "";
  if (s) {
    if (ve.includes(s))
      return `<${r}>`;
  } else return n;
  return `<${r}>${n}</${s}>`;
}
function ee(s = {}) {
  const e = /* @__PURE__ */ new Map(), {
    renderFn: t = F,
    textFn: o = _e,
    resolvers: r = {},
    optimizeImages: n = !1,
    keyedResolvers: a = !1
  } = s, c = t !== F, i = (l) => (h, d) => {
    const p = h.attrs || {};
    return d.render(l, p, h.children || null);
  }, u = (l, h) => {
    const { src: d, alt: p, title: b, srcset: w, sizes: v } = l.attrs || {};
    let k = d, T = {};
    if (n) {
      const { src: ue, attrs: he } = ge(d, n);
      k = ue, T = he;
    }
    const ce = {
      src: k,
      alt: p,
      title: b,
      srcset: w,
      sizes: v,
      ...T
    };
    return h.render("img", O(ce));
  }, f = (l, h) => {
    const { level: d, ...p } = l.attrs || {};
    return h.render(`h${d}`, p, l.children);
  }, y = (l, h) => {
    var d, p, b, w;
    const v = h.render("img", {
      src: (d = l.attrs) == null ? void 0 : d.fallbackImage,
      alt: (p = l.attrs) == null ? void 0 : p.alt,
      style: "width: 1.25em; height: 1.25em; vertical-align: text-top",
      draggable: "false",
      loading: "lazy"
    });
    return h.render("span", {
      "data-type": "emoji",
      "data-name": (b = l.attrs) == null ? void 0 : b.name,
      "data-emoji": (w = l.attrs) == null ? void 0 : w.emoji
    }, v);
  }, R = (l, h) => h.render(
    "pre",
    l.attrs || {},
    h.render("code", {}, l.children || "")
  ), _ = (l, h = !1) => ({ text: d, attrs: p }, b) => {
    const { class: w, id: v, ...k } = p || {}, T = h ? {
      class: w,
      id: v,
      style: we(k) || void 0
    } : p || {};
    return b.render(l, O(T), d);
  }, A = (l) => x(l), L = (l) => {
    const { marks: h, ...d } = l;
    if ("text" in l) {
      if (h)
        return h.reduce(
          (b, w) => A({ ...w, text: b }),
          A({ ...d, children: d.children })
        );
      const p = l.attrs || {};
      if (a) {
        const b = e.get("txt") || 0;
        e.set("txt", b + 1), p.key = `txt-${b}`;
      }
      return o(d.text, p);
    }
    return "";
  }, D = (l, h) => {
    const { linktype: d, href: p, anchor: b, ...w } = l.attrs || {};
    let v = "";
    switch (d) {
      case S.ASSET:
      case S.URL:
        v = p;
        break;
      case S.EMAIL:
        v = `mailto:${p}`;
        break;
      case S.STORY:
        v = p, b && (v = `${v}#${b}`);
        break;
      default:
        v = p;
        break;
    }
    const k = { ...w };
    return v && (k.href = v), h.render("a", k, l.text);
  }, re = (l, h) => {
    var d, p;
    return console.warn("[StoryblokRichtText] - BLOK resolver is not available for vanilla usage"), h.render("span", {
      blok: (d = l == null ? void 0 : l.attrs) == null ? void 0 : d.body[0],
      id: (p = l.attrs) == null ? void 0 : p.id,
      style: "display: none"
    });
  }, oe = (l, h) => {
    const d = {}, p = h.render("tbody", {}, l.children);
    return h.render("table", d, p);
  }, ne = (l, h) => {
    const d = {};
    return h.render("tr", d, l.children);
  }, ie = (l, h) => {
    const { colspan: d, rowspan: p, colwidth: b, backgroundColor: w, ...v } = l.attrs || {}, k = {
      ...v
    };
    d > 1 && (k.colspan = d), p > 1 && (k.rowspan = p);
    const T = [];
    return b && T.push(`width: ${b}px;`), w && T.push(`background-color: ${w};`), T.length > 0 && (k.style = T.join(" ")), h.render("td", O(k), l.children);
  }, ae = (l, h) => {
    const { colspan: d, rowspan: p, colwidth: b, backgroundColor: w, ...v } = l.attrs || {}, k = {
      ...v
    };
    d > 1 && (k.colspan = d), p > 1 && (k.rowspan = p);
    const T = [];
    return b && T.push(`width: ${b}px;`), w && T.push(`background-color: ${w};`), T.length > 0 && (k.style = T.join(" ")), h.render("th", O(k), l.children);
  }, B = /* @__PURE__ */ new Map([
    [g.DOCUMENT, i("")],
    [g.HEADING, f],
    [g.PARAGRAPH, i("p")],
    [g.UL_LIST, i("ul")],
    [g.OL_LIST, i("ol")],
    [g.LIST_ITEM, i("li")],
    [g.IMAGE, u],
    [g.EMOJI, y],
    [g.CODE_BLOCK, R],
    [g.HR, i("hr")],
    [g.BR, i("br")],
    [g.QUOTE, i("blockquote")],
    [g.COMPONENT, re],
    [Z.TEXT, L],
    [$.LINK, D],
    [$.ANCHOR, D],
    [$.STYLED, _("span", !0)],
    [$.BOLD, _("strong")],
    [$.TEXT_STYLE, _("span", !0)],
    [$.ITALIC, _("em")],
    [$.UNDERLINE, _("u")],
    [$.STRIKE, _("s")],
    [$.CODE, _("code")],
    [$.SUPERSCRIPT, _("sup")],
    [$.SUBSCRIPT, _("sub")],
    [$.HIGHLIGHT, _("mark")],
    [g.TABLE, oe],
    [g.TABLE_ROW, ne],
    [g.TABLE_CELL, ie],
    [g.TABLE_HEADER, ae]
  ]), M = new Map([
    ...B,
    ...Object.entries(r).map(([l, h]) => [l, h])
  ]), le = () => ({
    render: (l, h = {}, d) => {
      if (a && l) {
        const p = e.get(l) || 0;
        e.set(l, p + 1), h.key = `${l}-${p}`;
      }
      return t(l, h, d);
    },
    originalResolvers: B,
    mergedResolvers: M
  });
  function C(l) {
    const h = M.get(l.type);
    if (!h)
      return console.error("<Storyblok>", `No resolver found for node type ${l.type}`), "";
    const d = le();
    if (l.type === "text")
      return h(l, d);
    const p = l.content ? l.content.map(x) : void 0;
    return h({
      ...l,
      children: p
    }, d);
  }
  function x(l) {
    return l.type === "doc" ? c ? l.content.map(C) : l.content.map(C).join("") : Array.isArray(l) ? l.map(C) : C(l);
  }
  return {
    render: x
  };
}
let V = !1;
const z = [], Te = (s) => new Promise((e, t) => {
  if (typeof window > "u") {
    t(new Error("Cannot load Storyblok bridge: window is undefined (server-side environment)"));
    return;
  }
  if (window.storyblokRegisterEvent = (r) => {
    if (!window.location.search.includes("_storyblok")) {
      console.warn("You are not in Draft Mode or in the Visual Editor.");
      return;
    }
    V ? r() : z.push(r);
  }, document.getElementById("storyblok-javascript-bridge")) {
    e(void 0);
    return;
  }
  const o = document.createElement("script");
  o.async = !0, o.src = s, o.id = "storyblok-javascript-bridge", o.onerror = (r) => t(r), o.onload = (r) => {
    z.forEach((n) => n()), V = !0, e(r);
  }, document.getElementsByTagName("head")[0].appendChild(o);
});
var Re = Object.defineProperty, $e = (s, e, t) => e in s ? Re(s, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : s[e] = t, m = (s, e, t) => $e(s, typeof e != "symbol" ? e + "" : e, t);
class Ee extends Error {
  constructor(e) {
    super(e), this.name = "AbortError";
  }
}
function Ae(s, e, t) {
  if (!Number.isFinite(e))
    throw new TypeError("Expected `limit` to be a finite number");
  if (!Number.isFinite(t))
    throw new TypeError("Expected `interval` to be a finite number");
  const o = [];
  let r = [], n = 0, a = !1;
  const c = async () => {
    n++;
    const u = o.shift();
    if (u)
      try {
        const y = await s(...u.args);
        u.resolve(y);
      } catch (y) {
        u.reject(y);
      }
    const f = setTimeout(() => {
      n--, o.length > 0 && c(), r = r.filter((y) => y !== f);
    }, t);
    r.includes(f) || r.push(f);
  }, i = (...u) => a ? Promise.reject(
    new Error(
      "Throttled function is already aborted and not accepting new promises"
    )
  ) : new Promise((f, y) => {
    o.push({
      resolve: f,
      reject: y,
      args: u
    }), n < e && c();
  });
  return i.abort = () => {
    a = !0, r.forEach(clearTimeout), r = [], o.forEach(
      (u) => u.reject(() => new Ee("Throttle function aborted"))
    ), o.length = 0;
  }, i;
}
const G = (s = "") => s.includes("/cdn/"), Se = (s, e = 25, t = 1) => ({
  ...s,
  per_page: e,
  page: t
}), Ie = (s) => new Promise((e) => setTimeout(e, s)), Le = (s = 0, e) => Array.from({ length: s }, e), Ce = (s = 0, e = s) => {
  const t = Math.abs(e - s) || 0, o = s < e ? 1 : -1;
  return Le(t, (r, n) => n * o + s);
}, Oe = async (s, e) => Promise.all(s.map(e)), je = (s = [], e) => s.map(e).reduce((t, o) => [...t, ...o], []), U = (s, e, t) => {
  const o = [];
  for (const r in s) {
    if (!Object.prototype.hasOwnProperty.call(s, r))
      continue;
    const n = s[r];
    if (n == null)
      continue;
    const a = t ? "" : encodeURIComponent(r);
    let c;
    typeof n == "object" ? c = U(
      n,
      e ? e + encodeURIComponent(`[${a}]`) : a,
      Array.isArray(n)
    ) : c = `${e ? e + encodeURIComponent(`[${a}]`) : a}=${encodeURIComponent(n)}`, o.push(c);
  }
  return o.join("&");
}, q = (s) => {
  const e = {
    eu: "api.storyblok.com",
    us: "api-us.storyblok.com",
    cn: "app.storyblokchina.cn",
    ap: "api-ap.storyblok.com",
    ca: "api-ca.storyblok.com"
  };
  return e[s] ?? e.eu;
};
class Pe {
  constructor(e) {
    m(this, "baseURL"), m(this, "timeout"), m(this, "headers"), m(this, "responseInterceptor"), m(this, "fetch"), m(this, "ejectInterceptor"), m(this, "url"), m(this, "parameters"), m(this, "fetchOptions"), this.baseURL = e.baseURL, this.headers = e.headers || new Headers(), this.timeout = e != null && e.timeout ? e.timeout * 1e3 : 0, this.responseInterceptor = e.responseInterceptor, this.fetch = (...t) => e.fetch ? e.fetch(...t) : fetch(...t), this.ejectInterceptor = !1, this.url = "", this.parameters = {}, this.fetchOptions = {};
  }
  /**
   *
   * @param url string
   * @param params ISbStoriesParams
   * @returns Promise<ISbResponse | Error>
   */
  get(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("get");
  }
  post(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("post");
  }
  put(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("put");
  }
  delete(e, t) {
    return this.url = e, this.parameters = t ?? {}, this._methodHandler("delete");
  }
  async _responseHandler(e) {
    const t = [], o = {
      data: {},
      headers: {},
      status: 0,
      statusText: ""
    };
    e.status !== 204 && await e.json().then((r) => {
      o.data = r;
    });
    for (const r of e.headers.entries())
      t[r[0]] = r[1];
    return o.headers = { ...t }, o.status = e.status, o.statusText = e.statusText, o;
  }
  async _methodHandler(e) {
    let t = `${this.baseURL}${this.url}`, o = null;
    e === "get" ? t = `${this.baseURL}${this.url}?${U(this.parameters)}` : o = JSON.stringify(this.parameters);
    const r = new URL(t), n = new AbortController(), { signal: a } = n;
    let c;
    this.timeout && (c = setTimeout(() => n.abort(), this.timeout));
    try {
      const i = await this.fetch(`${r}`, {
        method: e,
        headers: this.headers,
        body: o,
        signal: a,
        ...this.fetchOptions
      });
      this.timeout && clearTimeout(c);
      const u = await this._responseHandler(
        i
      );
      return this.responseInterceptor && !this.ejectInterceptor ? this._statusHandler(this.responseInterceptor(u)) : this._statusHandler(u);
    } catch (i) {
      return {
        message: i
      };
    }
  }
  setFetchOptions(e = {}) {
    Object.keys(e).length > 0 && "method" in e && delete e.method, this.fetchOptions = { ...e };
  }
  eject() {
    this.ejectInterceptor = !0;
  }
  /**
   * Normalizes error messages from different response structures
   * @param data The response data that might contain error information
   * @returns A normalized error message string
   */
  _normalizeErrorMessage(e) {
    if (Array.isArray(e))
      return e[0] || "Unknown error";
    if (e && typeof e == "object") {
      if (e.error)
        return e.error;
      for (const t in e) {
        if (Array.isArray(e[t]))
          return `${t}: ${e[t][0]}`;
        if (typeof e[t] == "string")
          return `${t}: ${e[t]}`;
      }
      if (e.slug)
        return e.slug;
    }
    return "Unknown error";
  }
  _statusHandler(e) {
    const t = /20[0-6]/g;
    return new Promise((o, r) => {
      if (t.test(`${e.status}`))
        return o(e);
      const n = {
        message: this._normalizeErrorMessage(e.data),
        status: e.status,
        response: e
      };
      r(n);
    });
  }
}
const J = "SB-Agent", H = {
  defaultAgentName: "SB-JS-CLIENT",
  defaultAgentVersion: "SB-Agent-Version",
  packageVersion: "7.0.0"
}, xe = {
  PUBLISHED: "published"
};
let j = {};
const E = {};
class Ne {
  /**
   *
   * @param config ISbConfig interface
   * @param pEndpoint string, optional
   */
  constructor(e, t) {
    m(this, "client"), m(this, "maxRetries"), m(this, "retriesDelay"), m(this, "throttle"), m(this, "accessToken"), m(this, "cache"), m(this, "resolveCounter"), m(this, "relations"), m(this, "links"), m(this, "version"), m(this, "richTextResolver"), m(this, "resolveNestedRelations"), m(this, "stringifiedStoriesCache"), m(this, "inlineAssets");
    let o = e.endpoint || t;
    if (!o) {
      const a = e.https === !1 ? "http" : "https";
      e.oauthToken ? o = `${a}://${q(e.region)}/v1` : o = `${a}://${q(e.region)}/v2`;
    }
    const r = new Headers();
    r.set("Content-Type", "application/json"), r.set("Accept", "application/json"), e.headers && (e.headers.constructor.name === "Headers" ? e.headers.entries().toArray() : Object.entries(e.headers)).forEach(([a, c]) => {
      r.set(a, c);
    }), r.has(J) || (r.set(J, H.defaultAgentName), r.set(
      H.defaultAgentVersion,
      H.packageVersion
    ));
    let n = 5;
    e.oauthToken && (r.set("Authorization", e.oauthToken), n = 3), e.rateLimit && (n = e.rateLimit), this.maxRetries = e.maxRetries || 10, this.retriesDelay = 300, this.throttle = Ae(
      this.throttledRequest.bind(this),
      n,
      1e3
    ), this.accessToken = e.accessToken || "", this.relations = {}, this.links = {}, this.cache = e.cache || { clear: "manual" }, this.resolveCounter = 0, this.resolveNestedRelations = e.resolveNestedRelations || !0, this.stringifiedStoriesCache = {}, this.version = e.version || xe.PUBLISHED, this.inlineAssets = e.inlineAssets || !1, this.client = new Pe({
      baseURL: o,
      timeout: e.timeout || 0,
      headers: r,
      responseInterceptor: e.responseInterceptor,
      fetch: e.fetch
    });
  }
  parseParams(e) {
    return e.token || (e.token = this.getToken()), e.cv || (e.cv = E[e.token]), Array.isArray(e.resolve_relations) && (e.resolve_relations = e.resolve_relations.join(",")), typeof e.resolve_relations < "u" && (e.resolve_level = 2), e;
  }
  factoryParamOptions(e, t) {
    return G(e) ? this.parseParams(t) : t;
  }
  makeRequest(e, t, o, r, n) {
    const a = this.factoryParamOptions(
      e,
      Se(t, o, r)
    );
    return this.cacheResponse(e, a, void 0, n);
  }
  get(e, t = {}, o) {
    t || (t = {});
    const r = `/${e}`;
    G(r) && (t.version = t.version || this.version);
    const n = this.factoryParamOptions(r, t);
    return this.cacheResponse(r, n, void 0, o);
  }
  async getAll(e, t = {}, o, r) {
    const n = (t == null ? void 0 : t.per_page) || 25, a = `/${e}`.replace(/\/$/, ""), c = o ?? a.substring(a.lastIndexOf("/") + 1);
    t.version = t.version || this.version;
    const i = 1, u = await this.makeRequest(
      a,
      t,
      n,
      i,
      r
    ), f = u.total ? Math.ceil(u.total / n) : 1, y = await Oe(
      Ce(i, f),
      (R) => this.makeRequest(a, t, n, R + 1, r)
    );
    return je([u, ...y], (R) => Object.values(R.data[c]));
  }
  post(e, t = {}, o) {
    const r = `/${e}`;
    return this.throttle("post", r, t, o);
  }
  put(e, t = {}, o) {
    const r = `/${e}`;
    return this.throttle("put", r, t, o);
  }
  delete(e, t = {}, o) {
    t || (t = {});
    const r = `/${e}`;
    return this.throttle("delete", r, t, o);
  }
  getStories(e = {}, t) {
    return this._addResolveLevel(e), this.get("cdn/stories", e, t);
  }
  getStory(e, t = {}, o) {
    return this._addResolveLevel(t), this.get(`cdn/stories/${e}`, t, o);
  }
  getToken() {
    return this.accessToken;
  }
  ejectInterceptor() {
    this.client.eject();
  }
  _addResolveLevel(e) {
    typeof e.resolve_relations < "u" && (e.resolve_level = 2);
  }
  _cleanCopy(e) {
    return JSON.parse(JSON.stringify(e));
  }
  _insertLinks(e, t, o) {
    const r = e[t];
    r && r.fieldtype === "multilink" && r.linktype === "story" && typeof r.id == "string" && this.links[o][r.id] ? r.story = this._cleanCopy(this.links[o][r.id]) : r && r.linktype === "story" && typeof r.uuid == "string" && this.links[o][r.uuid] && (r.story = this._cleanCopy(this.links[o][r.uuid]));
  }
  /**
   *
   * @param resolveId A counter number as a string
   * @param uuid The uuid of the story
   * @returns string | object
   */
  getStoryReference(e, t) {
    return this.relations[e][t] ? JSON.parse(this.stringifiedStoriesCache[t] || JSON.stringify(this.relations[e][t])) : t;
  }
  /**
   * Resolves a field's value by replacing UUIDs with their corresponding story references
   * @param jtree - The JSON tree object containing the field to resolve
   * @param treeItem - The key of the field to resolve
   * @param resolveId - The unique identifier for the current resolution context
   *
   * This method handles both single string UUIDs and arrays of UUIDs:
   * - For single strings: directly replaces the UUID with the story reference
   * - For arrays: maps through each UUID and replaces with corresponding story references
   */
  _resolveField(e, t, o) {
    const r = e[t];
    typeof r == "string" ? e[t] = this.getStoryReference(o, r) : Array.isArray(r) && (e[t] = r.map(
      (n) => this.getStoryReference(o, n)
    ).filter(Boolean));
  }
  /**
   * Inserts relations into the JSON tree by resolving references
   * @param jtree - The JSON tree object to process
   * @param treeItem - The current field being processed
   * @param fields - The relation patterns to resolve (string or array of strings)
   * @param resolveId - The unique identifier for the current resolution context
   *
   * This method handles two types of relation patterns:
   * 1. Nested relations: matches fields that end with the current field name
   *    Example: If treeItem is "event_type", it matches patterns like "*.event_type"
   *
   * 2. Direct component relations: matches exact component.field patterns
   *    Example: "event.event_type" for component "event" and field "event_type"
   *
   * The method supports both string and array formats for the fields parameter,
   * allowing flexible specification of relation patterns.
   */
  _insertRelations(e, t, o, r) {
    if (Array.isArray(o) ? o.find((a) => a.endsWith(`.${t}`)) : o.endsWith(`.${t}`)) {
      this._resolveField(e, t, r);
      return;
    }
    const n = e.component ? `${e.component}.${t}` : t;
    (Array.isArray(o) ? o.includes(n) : o === n) && this._resolveField(e, t, r);
  }
  /**
   * Recursively traverses and resolves relations in the story content tree
   * @param story - The story object containing the content to process
   * @param fields - The relation patterns to resolve
   * @param resolveId - The unique identifier for the current resolution context
   */
  iterateTree(e, t, o) {
    const r = (n, a = "") => {
      if (!(!n || n._stopResolving)) {
        if (Array.isArray(n))
          n.forEach((c, i) => r(c, `${a}[${i}]`));
        else if (typeof n == "object")
          for (const c in n) {
            const i = a ? `${a}.${c}` : c;
            (n.component && n._uid || n.type === "link") && (this._insertRelations(n, c, t, o), this._insertLinks(n, c, o)), r(n[c], i);
          }
      }
    };
    r(e.content);
  }
  async resolveLinks(e, t, o) {
    let r = [];
    if (e.link_uuids) {
      const n = e.link_uuids.length, a = [], c = 50;
      for (let i = 0; i < n; i += c) {
        const u = Math.min(n, i + c);
        a.push(e.link_uuids.slice(i, u));
      }
      for (let i = 0; i < a.length; i++)
        (await this.getStories({
          per_page: c,
          language: t.language,
          version: t.version,
          starts_with: t.starts_with,
          by_uuids: a[i].join(",")
        })).data.stories.forEach(
          (u) => {
            r.push(u);
          }
        );
    } else
      r = e.links;
    r.forEach((n) => {
      this.links[o][n.uuid] = {
        ...n,
        _stopResolving: !0
      };
    });
  }
  async resolveRelations(e, t, o) {
    let r = [];
    if (e.rel_uuids) {
      const n = e.rel_uuids.length, a = [], c = 50;
      for (let i = 0; i < n; i += c) {
        const u = Math.min(n, i + c);
        a.push(e.rel_uuids.slice(i, u));
      }
      for (let i = 0; i < a.length; i++)
        (await this.getStories({
          per_page: c,
          language: t.language,
          version: t.version,
          starts_with: t.starts_with,
          by_uuids: a[i].join(","),
          excluding_fields: t.excluding_fields
        })).data.stories.forEach((u) => {
          r.push(u);
        });
      r.length > 0 && (e.rels = r, delete e.rel_uuids);
    } else
      r = e.rels;
    r && r.length > 0 && r.forEach((n) => {
      this.relations[o][n.uuid] = {
        ...n,
        _stopResolving: !0
      };
    });
  }
  /**
   *
   * @param responseData
   * @param params
   * @param resolveId
   * @description Resolves the relations and links of the stories
   * @returns Promise<void>
   *
   */
  async resolveStories(e, t, o) {
    var r, n;
    let a = [];
    if (this.links[o] = {}, this.relations[o] = {}, typeof t.resolve_relations < "u" && t.resolve_relations.length > 0 && (typeof t.resolve_relations == "string" && (a = t.resolve_relations.split(",")), await this.resolveRelations(e, t, o)), t.resolve_links && ["1", "story", "url", "link"].includes(t.resolve_links) && ((r = e.links) != null && r.length || (n = e.link_uuids) != null && n.length) && await this.resolveLinks(e, t, o), this.resolveNestedRelations)
      for (const c in this.relations[o])
        this.iterateTree(
          this.relations[o][c],
          a,
          o
        );
    e.story ? this.iterateTree(e.story, a, o) : e.stories.forEach((c) => {
      this.iterateTree(c, a, o);
    }), this.stringifiedStoriesCache = {}, delete this.links[o], delete this.relations[o];
  }
  async cacheResponse(e, t, o, r) {
    const n = U({ url: e, params: t }), a = this.cacheProvider();
    if (t.version === "published" && e !== "/cdn/spaces/me") {
      const c = await a.get(n);
      if (c)
        return Promise.resolve(c);
    }
    return new Promise(async (c, i) => {
      var u;
      try {
        const f = await this.throttle(
          "get",
          e,
          t,
          r
        );
        if (f.status !== 200)
          return i(f);
        let y = { data: f.data, headers: f.headers };
        if ((u = f.headers) != null && u["per-page"] && (y = Object.assign({}, y, {
          perPage: f.headers["per-page"] ? Number.parseInt(f.headers["per-page"]) : 0,
          total: f.headers["per-page"] ? Number.parseInt(f.headers.total) : 0
        })), y.data.story || y.data.stories) {
          const _ = this.resolveCounter = ++this.resolveCounter % 1e3;
          await this.resolveStories(y.data, t, `${_}`), y = await this.processInlineAssets(y);
        }
        t.version === "published" && e !== "/cdn/spaces/me" && await a.set(n, y);
        const R = this.cache.clear === "onpreview" && t.version === "draft" || this.cache.clear === "auto";
        return t.token && y.data.cv && (R && E[t.token] && E[t.token] !== y.data.cv && await this.flushCache(), E[t.token] = y.data.cv), c(y);
      } catch (f) {
        if (f.response && f.status === 429 && (o = typeof o > "u" ? 0 : o + 1, o < this.maxRetries))
          return console.log(
            `Hit rate limit. Retrying in ${this.retriesDelay / 1e3} seconds.`
          ), await Ie(this.retriesDelay), this.cacheResponse(e, t, o).then(c).catch(i);
        i(f);
      }
    });
  }
  throttledRequest(e, t, o, r) {
    return this.client.setFetchOptions(r), this.client[e](t, o);
  }
  cacheVersions() {
    return E;
  }
  cacheVersion() {
    return E[this.accessToken];
  }
  setCacheVersion(e) {
    this.accessToken && (E[this.accessToken] = e);
  }
  clearCacheVersion() {
    this.accessToken && (E[this.accessToken] = 0);
  }
  cacheProvider() {
    switch (this.cache.type) {
      case "memory":
        return {
          get(e) {
            return Promise.resolve(j[e]);
          },
          getAll() {
            return Promise.resolve(j);
          },
          set(e, t) {
            return j[e] = t, Promise.resolve(void 0);
          },
          flush() {
            return j = {}, Promise.resolve(void 0);
          }
        };
      case "custom":
        if (this.cache.custom)
          return this.cache.custom;
      // eslint-disable-next-line no-fallthrough
      default:
        return {
          get() {
            return Promise.resolve();
          },
          getAll() {
            return Promise.resolve(void 0);
          },
          set() {
            return Promise.resolve(void 0);
          },
          flush() {
            return Promise.resolve(void 0);
          }
        };
    }
  }
  async flushCache() {
    return await this.cacheProvider().flush(), this.clearCacheVersion(), this;
  }
  async processInlineAssets(e) {
    if (!this.inlineAssets)
      return e;
    const t = (o) => {
      if (!o || typeof o != "object")
        return o;
      if (Array.isArray(o))
        return o.map((n) => t(n));
      let r = { ...o };
      r.fieldtype === "asset" && Array.isArray(e.data.assets) && (r = {
        ...r,
        ...e.data.assets.find((n) => n.id === r.id)
      });
      for (const n in r)
        typeof r[n] == "object" && (r[n] = t(r[n]));
      return r;
    };
    return e.data.story && (e.data.story.content = t(e.data.story.content)), e.data.stories && (e.data.stories = e.data.stories.map((o) => (o.content = t(o.content), o))), e;
  }
}
const Ge = (s = {}) => {
  const { apiOptions: e } = s;
  if (!e || !e.accessToken) {
    console.error(
      "You need to provide an access token to interact with Storyblok API. Read https://www.storyblok.com/docs/api/content-delivery#topics/authentication"
    );
    return;
  }
  return { storyblokApi: new Ne(e) };
}, He = (s) => {
  if (typeof s != "object" || typeof s._editable > "u")
    return {};
  try {
    const e = JSON.parse(
      s._editable.replace(/^<!--#storyblok#/, "").replace(/-->$/, "")
    );
    return e ? {
      "data-blok-c": JSON.stringify(e),
      "data-blok-uid": `${e.id}-${e.uid}`
    } : {};
  } catch {
    return {};
  }
};
let K = "https://app.storyblok.com/f/storyblok-v2-latest.js";
const Ue = (s, e, t = {}) => {
  var o;
  const r = !(typeof window > "u") && typeof window.storyblokRegisterEvent < "u", n = new URL((o = window.location) == null ? void 0 : o.href).searchParams.get(
    "_storyblok"
  ), a = n !== null && +n === s;
  if (!(!r || !a)) {
    if (!s) {
      console.warn("Story ID is not defined. Please provide a valid ID.");
      return;
    }
    window.storyblokRegisterEvent(() => {
      new window.StoryblokBridge(t).on(["input", "published", "change"], (c) => {
        var i;
        c && (c.action === "input" && ((i = c.story) == null ? void 0 : i.id) === s ? e(c.story) : (c.action === "change" || c.action === "published") && c.storyId === s && window.location.reload());
      });
    });
  }
}, De = (s = {}) => {
  var e, t;
  const {
    bridge: o,
    accessToken: r,
    use: n = [],
    apiOptions: a = {},
    bridgeUrl: c
  } = s;
  a.accessToken = a.accessToken || r;
  const i = { bridge: o, apiOptions: a };
  let u = {};
  n.forEach((y) => {
    u = { ...u, ...y(i) };
  }), c && (K = c);
  const f = !(typeof window > "u") && ((t = (e = window.location) == null ? void 0 : e.search) == null ? void 0 : t.includes("_storyblok_tk"));
  return o !== !1 && f && Te(K), u;
};
function qe(s, e) {
  return ee(e).render(s);
}
const te = /* @__PURE__ */ Y({
  __name: "StoryblokComponent",
  props: {
    blok: {}
  },
  setup(s, { expose: e }) {
    var c;
    const t = s, o = P();
    e({
      value: o
    });
    const r = typeof N(t.blok.component) != "string", n = de("VueSDKOptions"), a = P((c = t.blok.component) == null ? void 0 : c.replace(/_/g, "-"));
    return !r && n && (n.enableFallbackComponent ? (a.value = n.customFallbackComponent ?? "FallbackComponent", typeof N(a.value) == "string" && console.error(
      `Is the Fallback component "${a.value}" registered properly?`
    )) : console.error(
      `Component could not be found for blok "${t.blok.component}"! Is it defined in main.ts as "app.component("${t.blok.component}", ${t.blok.component});"?`
    )), (i, u) => (X(), W(N(a.value), pe({
      ref_key: "blokRef",
      ref: o
    }, { ...i.$props, ...i.$attrs }), null, 16));
  }
}), Be = (s) => {
  var e, t;
  return Q(
    te,
    {
      blok: (e = s == null ? void 0 : s.attrs) == null ? void 0 : e.body[0],
      id: (t = s.attrs) == null ? void 0 : t.id
    },
    s.children
  );
};
function Me(s) {
  const e = {
    renderFn: Q,
    // TODO: Check why this changed.
    // @ts-expect-error - createTextVNode types has been recently changed.
    textFn: fe,
    keyedResolvers: !0,
    resolvers: {
      [g.COMPONENT]: Be,
      ...s.resolvers
    }
  };
  return ee(e);
}
const Fe = /* @__PURE__ */ Y({
  __name: "StoryblokRichText",
  props: {
    doc: {},
    resolvers: {}
  },
  setup(s) {
    const e = s, t = P(), o = () => t.value;
    return ye([() => e.doc, () => e.resolvers], ([r, n]) => {
      const { render: a } = Me({
        resolvers: n ?? {}
      });
      t.value = a(r);
    }, {
      immediate: !0,
      deep: !0
    }), (r, n) => (X(), W(o));
  }
}), Ve = {
  beforeMount(s, e) {
    if (e.value) {
      const t = He(e.value);
      Object.keys(t).length > 0 && (s.setAttribute("data-blok-c", t["data-blok-c"]), s.setAttribute("data-blok-uid", t["data-blok-uid"]), s.classList.add("storyblok__outline"));
    }
  }
}, se = (s) => {
  console.error(`You can't use ${s} if you're not loading apiPlugin. Please provide it on StoryblokVue initialization.
    `);
};
let I = null;
const Je = () => (I || se("useStoryblokApi"), I), Ke = async (s, e = {}, t = {}) => {
  const o = P(null);
  if (t.resolveRelations = t.resolveRelations ?? e.resolve_relations, t.resolveLinks = t.resolveLinks ?? e.resolve_links, me(() => {
    o.value && o.value.id && Ue(
      o.value.id,
      (r) => o.value = r,
      t
    );
  }), I) {
    const { data: r } = await I.get(
      `cdn/stories/${s}`,
      e
    );
    o.value = r.story;
  } else
    se("useStoryblok");
  return o;
}, Ye = {
  install(s, e = {}) {
    s.directive("editable", Ve), s.component("StoryblokComponent", te), s.component("StoryblokRichText", Fe), e.enableFallbackComponent && !e.customFallbackComponent && s.component(
      "FallbackComponent",
      be(() => import("./FallbackComponent-Dky11gEu.js"))
    );
    const { storyblokApi: t } = De(e);
    I = t || null, s.provide("VueSDKOptions", e);
  }
};
export {
  g as BlockTypes,
  $ as MarkTypes,
  te as StoryblokComponent,
  Fe as StoryblokRichText,
  Ye as StoryblokVue,
  Z as TextTypes,
  Ge as apiPlugin,
  qe as renderRichText,
  ee as richTextResolver,
  Ke as useStoryblok,
  Je as useStoryblokApi,
  Ue as useStoryblokBridge,
  Me as useStoryblokRichText
};
